import React, { useContext, useEffect } from 'react'
import QuizSectionTemplate from '../QuizSectionTemplate'
import Explaination from '../Explaination'
import {
    Flex,
    Group,
    LoadingOverlay,
    Select,
    Stack,
    TextInput,
} from '@mantine/core'
import QuizParameterTemplate from '../QuizParameterTemplate'
import CustomNumberInput from '@components/inputs/quiz/CustomNumberInput'
import { QuizContext } from '@context/QuizContext'
import RoundButton from '@components/button/RoundButton'
import accounting from 'accounting'
import BackButton from '../BackButton'
import QuizTooltip from '../QuizTooltip'
import { Checkbox } from '@mantine/core'
import Quiz from '@api/Quiz'

const QuizSectionFour = () => {
    const {
        form4,
        nextStep,
        currentStep,
        years_before_retirement,
        estimated_saving_source_for_retirement,
        quiz_id,
    } = useContext(QuizContext)

    const { isLoading, onSubmit } = Quiz.useUpdateQuiz({
        form: form4,
        aftersuccess: () => nextStep!(),
    })

    if (quiz_id) form4!.values.quiz_id = quiz_id

    if (currentStep !== 4) {
        return <></>
    }

    return (
        <form
            onSubmit={(e) => {
                onSubmit()
                e.preventDefault()
            }}
        >
            <LoadingOverlay visible={isLoading} />
            <Explaination />

            <QuizSectionTemplate
                section={4}
                title='ประมาณการมูลค่าแหล่งเงินออมเพื่อการเกษียณที่เตรียมไว้แล้ว'
            >
                <QuizParameterTemplate
                    afterLabel='บาท'
                    label='เงินกองทุนสำรองเลี้ยงชีพ / กบข'
                    description='(โปรดตรวจสอบจาก Application ที่ท่านใช้งาน)'
                >
                    <CustomNumberInput
                        min={0}
                        thousandSeparator=','
                        {...form4!.getInputProps('provident_fund')}
                    />
                </QuizParameterTemplate>
                <QuizParameterTemplate
                    afterLabel='บาท'
                    label={`สินทรัพย์ลงทุนเพื่อบรรลุเป้าหมายเกษียณ 
                    เช่น RMF SSF หรือสินทรัพย์ลงทุนอื่นๆ ที่มีในปัจจุบัน`}
                >
                    <CustomNumberInput
                        min={0}
                        thousandSeparator=','
                        {...form4!.getInputProps(
                            'assets_invested_to_reach_retirement_goal'
                        )}
                    />
                </QuizParameterTemplate>
                <QuizParameterTemplate
                    afterLabel='% ต่อปี'
                    required
                    label={`อัตราผลตอบแทนคาดหวังก่อนเกษียณ`}
                >
                    <QuizTooltip label='ระหว่าง 2.50% - 8.00% ต่อปี'>
                        <Select
                            w={200}
                            styles={{
                                input: {
                                    borderColor: 'var(--mantine-color-gray-2)',
                                    textAlign: 'end',
                                },
                            }}
                            data={[
                                { label: '2.50%', value: '2.50' },
                                { label: '3.00%', value: '3.00' },
                                { label: '3.50%', value: '3.50' },
                                { label: '4.00%', value: '4.00' },
                                { label: '4.50%', value: '4.50' },
                                { label: '5.00%', value: '5.00' },
                                { label: '5.50%', value: '5.50' },
                                { label: '6.00%', value: '6.00' },
                                { label: '6.50%', value: '6.50' },
                                { label: '7.00%', value: '7.00' },
                                { label: '7.50%', value: '7.50' },
                                { label: '8.00%', value: '8.00' },
                            ]}
                            {...form4!.getInputProps(
                                'expected_rate_of_return_before_retirement'
                            )}
                        />
                    </QuizTooltip>
                </QuizParameterTemplate>

                <QuizParameterTemplate label={`ประกันชีวิต`}>
                    <Group>
                        <Checkbox
                            checked={form4!.values.life_insurance_mtl}
                            onChange={(e) => {
                                form4!.setFieldValue(
                                    'life_insurance_mtl',
                                    e.target.checked ? true : false
                                )
                            }}
                            label='ประกัน MTL'
                        ></Checkbox>
                        <Checkbox
                            checked={form4!.values.life_insurance_other}
                            onChange={(e) => {
                                form4!.setFieldValue(
                                    'life_insurance_other',
                                    e.target.checked ? true : false
                                )
                            }}
                            label='ประกันบริษัทอื่น ๆ'
                        ></Checkbox>
                    </Group>
                </QuizParameterTemplate>

                <QuizParameterTemplate
                    afterLabel='บาท'
                    label={`สินทรัพย์อื่น ๆ ที่ต้องการคํานวณเพื่อบรรลุเป้าหมายเกษียณ เช่น เงินฝาก, ฉลากออมทรัพย์, ทองคํา, มรดก และอื่น ๆ`}
                    description='(ไม่แนะนําให้ใช้บ้านและที่ดินอยู่อาศัยร่วมคํานวณ)'
                >
                    <CustomNumberInput
                        min={0}
                        thousandSeparator=','
                        {...form4!.getInputProps('other_assets')}
                    />
                </QuizParameterTemplate>
                <QuizParameterTemplate
                    afterLabel='ปี'
                    label='ระยะเวลาก่อนเกษียณ'
                    type='value'
                >
                    {years_before_retirement}
                </QuizParameterTemplate>
                <QuizParameterTemplate
                    afterLabel='บาท'
                    label={`ประมาณการมูลค่าแหล่งเงินออมเพื่อการเกษียณที่เตรียมไว้แล้ว ณ ปีที่เกษียณอายุ`}
                    type='conclude'
                >
                    {accounting.formatMoney(
                        estimated_saving_source_for_retirement!,
                        {
                            symbol: '',
                            precision: 2,
                        }
                    ) ?? 0}
                </QuizParameterTemplate>
            </QuizSectionTemplate>
            <Flex
                mb='xl'
                direction={{ xs: 'column', sm: 'row' }}
                gap='lg'
                justify={{ xs: 'center', sm: 'space-between' }}
            >
                <BackButton />
                <RoundButton type='submit' label='ถัดไป' />
            </Flex>
        </form>
    )
}

export default QuizSectionFour
